import React, { useState } from 'react';
import { ButtonGroup, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge, Alert, Progress } from 'reactstrap';

const BulkActions = ({
  selectedItems = [],
  totalItems = 0,
  onSelectAll,
  onDeselectAll,
  actions = [],
  onAction,
  isLoading = false,
  className = ""
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionData, setActionData] = useState({});
  const [bulkProgress, setBulkProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleActionClick = (action) => {
    if (action.requiresConfirmation) {
      setSelectedAction(action);
      setShowModal(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = async (action, data = {}) => {
    setIsProcessing(true);
    setBulkProgress(0);

    try {
      if (action.withProgress) {
        // Simulate progress for bulk operations
        for (let i = 0; i <= selectedItems.length; i++) {
          setBulkProgress(Math.round((i / selectedItems.length) * 100));
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      await onAction(action.key, selectedItems, data);
      setShowModal(false);
      setActionData({});
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
      setBulkProgress(0);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      delete: 'fa-trash',
      activate: 'fa-check-circle',
      deactivate: 'fa-times-circle',
      start: 'fa-play',
      stop: 'fa-stop',
      restart: 'fa-refresh',
      email: 'fa-envelope',
      export: 'fa-download',
      edit: 'fa-edit',
      assign: 'fa-user-plus',
      move: 'fa-arrows',
      archive: 'fa-archive',
      approve: 'fa-thumbs-up',
      reject: 'fa-thumbs-down'
    };
    return icons[actionType] || 'fa-cog';
  };

  const getActionColor = (actionType) => {
    const colors = {
      delete: 'danger',
      activate: 'success',
      deactivate: 'warning',
      start: 'success',
      stop: 'warning',
      restart: 'info',
      email: 'primary',
      export: 'secondary',
      edit: 'info',
      assign: 'primary',
      move: 'secondary',
      archive: 'warning',
      approve: 'success',
      reject: 'danger'
    };
    return colors[actionType] || 'secondary';
  };

  if (selectedItems.length === 0) return null;

  return (
    <div className={`header-table-filters advanced-table-filters style-filter-show ${className}`}>
      <div className='left-area'>
        <p>{selectedItems.length} of {totalItems} selected
          {' '}
          <a
            href="#"
            onClick={onSelectAll}
            disabled={isLoading || isProcessing}
            style={{ color: 'var(--primary-color)', fontSize: '12px', position: 'relative', }}
          >
            Select All
          </a>
          <a
            href="#"
            onClick={onDeselectAll}
            disabled={isLoading || isProcessing}
            style={{ color: 'var(--primary-color)', fontSize: '12px', position: 'relative', }}
          >
            Clear Selection
          </a>
        </p>
        {actions.slice(0, 3).map(action => (
          <button className='assing-btn d-flex' key={action.key} color={getActionColor(action.type)} onClick={() => handleActionClick(action)} disabled={isLoading || isProcessing} title={action.description}>
            <i className={`icon fa ${getActionIcon(action.type)}`}></i>
            {action.label}
          </button>
        ))}

        {actions.length > 3 && (
          <UncontrolledDropdown>
            <DropdownToggle
              size="sm"
              color="secondary"
              caret
              disabled={isLoading || isProcessing}
            >
              More Actions
              <i class="fa fa-angle-down"></i>
            </DropdownToggle>
            <DropdownMenu right>
              {actions.slice(3).map(action => (
                <DropdownItem
                  key={action.key}
                  onClick={() => handleActionClick(action)}
                >
                  <i className={`fa ${getActionIcon(action.type)} mr-2`}></i>
                  {action.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
        {isProcessing && (
          <div className="mr-3" style={{ width: '150px' }}>
            <Progress value={bulkProgress} className="mb-1" />
            <small className="text-muted">{bulkProgress}% complete</small>
          </div>
        )}
      </div>
      <div className='right-side-filter'>
        <button className='close' onClick={onDeselectAll}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="path-1-inside-1_1030_1913" fill="white">
              <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
            </mask>
            <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
            <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
          </svg>
        </button>
      </div>
      {/* <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded mb-3">
        <div className="d-flex align-items-center">
          <Badge color="primary" className="mr-3">
            {selectedItems.length} of {totalItems} selected
          </Badge>

          <ButtonGroup size="sm">
            <Button
              color="outline-secondary"
              onClick={onSelectAll}
              disabled={isLoading || isProcessing}
            >
              Select All
            </Button>
            <Button
              color="outline-secondary"
              onClick={onDeselectAll}
              disabled={isLoading || isProcessing}
            >
              Clear Selection
            </Button>
          </ButtonGroup>
        </div>

        <div className="d-flex align-items-center">
          {isProcessing && (
            <div className="mr-3" style={{ width: '150px' }}>
              <Progress value={bulkProgress} className="mb-1" />
              <small className="text-muted">{bulkProgress}% complete</small>
            </div>
          )}

          <ButtonGroup size="sm">

          </ButtonGroup>
        </div>
      </div> */}

      {/* Confirmation/Input Modal */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowModal(false)}>
          <h3>{selectedAction?.label} - {selectedItems.length} items</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            {selectedAction?.warning && (
              <Alert color="warning">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                {selectedAction.warning}
              </Alert>
            )}

            <p>{selectedAction?.confirmationMessage || `Are you sure you want to ${selectedAction?.label?.toLowerCase()} ${selectedItems.length} items?`}</p>

            {selectedAction?.requiresInput && selectedAction?.inputOptions && (
              <Form>
                <FormGroup>
                  <Label>Select Option</Label>
                  <Input
                    type="select"
                    value={actionData.selectedOption || ''}
                    onChange={(e) => setActionData({ ...actionData, selectedOption: e.target.value })}
                    required
                  >
                    <option value="">Choose...</option>
                    {selectedAction.inputOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Form>
            )}

            {selectedAction?.fields && (
              <Form>
                {selectedAction.fields.map(field => (
                  <FormGroup key={field.key}>
                    <Label>{field.label}</Label>
                    {field.type === 'select' ? (
                      <Input
                        type="select"
                        value={actionData[field.key] || ''}
                        onChange={(e) => setActionData({ ...actionData, [field.key]: e.target.value })}
                        required={field.required}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <Input
                        type={field.type || 'text'}
                        value={actionData[field.key] || ''}
                        onChange={(e) => setActionData({ ...actionData, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                  </FormGroup>
                ))}
              </Form>
            )}

            {isProcessing && (
              <div className="mt-3">
                <Progress value={bulkProgress} />
                <p className="text-center mt-2">Processing... {bulkProgress}%</p>
              </div>
            )}
            <div className="group-form btn-group">
                <button className="btn-style-border" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-style-new" style={{backgroundColor: getActionColor(selectedAction?.type)}} onClick={() => executeAction(selectedAction, actionData)} disabled={isProcessing}>{isProcessing ? 'Processing...' : selectedAction?.label}</button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default BulkActions;